import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Header.css";

/* ===============================
   MENU (IDs normalisés, sans espaces/accents)
   =============================== */
const MENU = {
  appareils: [{ label: "Qui sommes-nous", href: "#a-propos" }],
  services: {
    left: [
      { label: "Ascenseurs-monte-charges", href: "#ascenseurs-monte-charges" },
      { label: "Controle-tubes", href: "#controle-tubes" },
      { label: "Controle-cables", href: "#controle-cables" },
      { label: "Thermographie-infrarouge", href: "#thermographie-infrarouge" },
      { label: "Controle-non-destructif", href: "#controle-non-destructif" },
    ],
    middle: [
      { label: "Assistance-Technique", href: "#assistance-technique" },
      { label: "Expertise-maritime", href: "#maritime" },
      { label: "Formation (Academy)", href: "#formation" },
      { label: "Installations-électriques", href: "#installations-electriques" },
      // Rappel : ancres uniques -> pas de doublon d’IDs
      { label: "Ascenseurs & monte-charges", href: "#ascenseurs-monte-charges" },
    ],
    right: [
      { label: "Contrôle-de-construction", href: "#controle-de-construction" },
      { label: "Appareils à pression", href: "#appareils-a-pression" },
      { label: "Appareils de levage", href: "#appareils-de-levage" },
      { label: "Assistance technique", href: "#assistance-technique" },
    ],
    cta: {
      title: "Besoin d’un devis ? Parlons de votre projet.",
      button: { label: "DEMANDER UN DEVIS", href: "#contact" },
    },
  },
  accessoires: [
    { label: "Palonniers", href: "#palo" },
    { label: "Élingues & accessoires", href: "#elingage" },
    { label: "Armoires & TGBT", href: "#armoires" },
  ],
  vousetes: [{ label: "Expertise", href: "#atouts" }],
};

export default function Header() {
  const [open, setOpen] = useState(false);   // menu burger (mobile)
  const [mega, setMega] = useState("");      // méga-menu ouvert
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Fermer menu au resize + clic extérieur
  useEffect(() => {
    const onResize = () => window.innerWidth > 1024 && setOpen(false);
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setMega("");
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("click", onDoc);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("click", onDoc);
    };
  }, []);

  // Scroll fluide avec offset du header sticky (utilisé sur la Home)
  const smoothScrollInHome = (hash) => {
    const el = document.querySelector(hash);
    if (!el) return;
    const offset = 90; // adapte à la hauteur réelle du header
    const y = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: y, behavior: "smooth" });
    setOpen(false);
    setMega("");
  };

  // Navigation intelligente : scrolle si on est sur "/", sinon route vers "/#id"
  const goTo = (href) => {
    if (!href?.startsWith("#")) return;
    if (location.pathname === "/") {
      smoothScrollInHome(href);
    } else {
      navigate(`/${href}`); // ex: "/#contact"
      setOpen(false);
      setMega("");
    }
  };

  const NavLink = ({ href, children, className, onClick }) => (
    <a
      href={href}
      className={className}
      onClick={(e) => {
        if (href?.startsWith("#")) {
          e.preventDefault();
          goTo(href);
        }
        onClick && onClick(e);
      }}
    >
      {children}
    </a>
  );

  return (
    <header className="hm-header" ref={wrapRef}>
      <div className="hm-topbar">
        {/* Logo */}
        <div className="hm-logo">
          <a href="/" aria-label="Accueil">
            <img src="/images/safety-veratra.png" alt="VERATRAC SAFETY" />
          </a>
        </div>

        {/* Burger */}
        <button
          className={`hm-burger ${open ? "is-open" : ""}`}
          aria-label="Ouvrir le menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span /><span /><span />
        </button>

        {/* Nav principale */}
        <nav className={`hm-nav ${open ? "is-open" : ""}`} aria-label="Navigation principale">
          {/* VERATRAC-SAFETY */}
          <div
            className="hm-item"
            onMouseEnter={() => setMega("appareils")}
            onMouseLeave={() => setMega("")}
          >
            <button
              className="hm-link"
              aria-haspopup="true"
              aria-expanded={mega === "appareils"}
              onClick={() => setMega((v) => (v === "appareils" ? "" : "appareils"))}
            >
              VERATRAC-SAFETY
              <svg viewBox="0 0 24 24" className="hm-caret" aria-hidden="true">
                <path d="M7 10l5 5 5-5H7z" />
              </svg>
            </button>

            <div className={`hm-mega hm-mega--simple ${mega === "appareils" ? "is-open" : ""}`}>
              <ul className="hm-col">
                {MENU.appareils.map((it) => (
                  <li key={it.label}>
                    <NavLink href={it.href} className="hm-sub">{it.label}</NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* NOS SERVICES */}
          <div
            className="hm-item"
            onMouseEnter={() => setMega("services")}
            onMouseLeave={() => setMega("")}
          >
            <button
              className="hm-link"
              aria-haspopup="true"
              aria-expanded={mega === "services"}
              onClick={() => setMega((v) => (v === "services" ? "" : "services"))}
            >
              NOS SERVICES
              <svg viewBox="0 0 24 24" className="hm-caret" aria-hidden="true">
                <path d="M7 10l5 5 5-5H7z" />
              </svg>
            </button>

            <div className={`hm-mega ${mega === "services" ? "is-open" : ""}`}>
              <ul className="hm-col">
                {MENU.services.left.map((it) => (
                  <li key={it.label}>
                    <NavLink href={it.href} className="hm-sub">{it.label}</NavLink>
                  </li>
                ))}
              </ul>

              <ul className="hm-col">
                {MENU.services.middle.map((it) => (
                  <li key={it.label}>
                    <NavLink href={it.href} className="hm-sub">{it.label}</NavLink>
                  </li>
                ))}
              </ul>

              {MENU.services.right?.length > 0 && (
                <ul className="hm-col">
                  {MENU.services.right.map((it) => (
                    <li key={it.label}>
                      <NavLink href={it.href} className="hm-sub">{it.label}</NavLink>
                    </li>
                  ))}
                </ul>
              )}

              <div className="hm-col hm-cta">
                <p className="hm-cta-title">{MENU.services.cta.title}</p>
                <NavLink href={MENU.services.cta.button.href} className="hm-cta-btn">
                  {MENU.services.cta.button.label}
                </NavLink>
              </div>
            </div>
          </div>

          

            

          {/* NOTRE EXPERTISE */}
          <div
            className="hm-item"
            onMouseEnter={() => setMega("vousetes")}
            onMouseLeave={() => setMega("")}
          >
            <button
              className="hm-link"
              aria-haspopup="true"
              aria-expanded={mega === "vousetes"}
              onClick={() => setMega((v) => (v === "vousetes" ? "" : "vousetes"))}
            >
              NOTRE EXPERTISE
              <svg viewBox="0 0 24 24" className="hm-caret" aria-hidden="true">
                <path d="M7 10l5 5 5-5H7z" />
              </svg>
            </button>

            <div className={`hm-mega hm-mega--simple ${mega === "vousetes" ? "is-open" : ""}`}>
              <ul className="hm-col">
                {MENU.vousetes.map((it) => (
                  <li key={it.label}>
                    <NavLink href={it.href} className="hm-sub">{it.label}</NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA Contact */}
          <NavLink href="#contact" className="hm-devis">CONTACT</NavLink>
        </nav>
      </div>
    </header>
  );
}
